import type { KeystrokeEvent } from "../measurement";
import type {
  MasteryBand,
  SkillMastery,
  SkillObservation,
  SkillType,
} from "./types";

const MINIMUM_ATTEMPTS: Record<SkillType, number> = {
  key: 8,
  shiftedKey: 8,
  bigram: 5,
  trigram: 4,
  word: 3,
  punctuation: 8,
  number: 8,
  layoutReach: 8,
  contentSkill: 8,
};

function round(value: number, decimals = 2): number {
  const multiplier = 10 ** decimals;
  return Math.round(value * multiplier) / multiplier;
}

function percentile(values: readonly number[], percentileValue: number): number | null {
  const usable = values
    .filter((value) => Number.isFinite(value) && value >= 0)
    .sort((left, right) => left - right);
  if (usable.length === 0) {
    return null;
  }
  const index = Math.min(
    usable.length - 1,
    Math.max(0, Math.ceil((percentileValue / 100) * usable.length) - 1),
  );
  return round(usable[index] ?? 0);
}

function classifyKey(expected: string): SkillType {
  if (/[A-Z]/.test(expected)) return "shiftedKey";
  if (/[0-9]/.test(expected)) return "number";
  if (/[^\p{L}\p{N}\s]/u.test(expected)) return "punctuation";
  return "key";
}

function keySkillId(expected: string): string {
  const readable = expected === " " ? "space" : expected;
  return `${classifyKey(expected)}:${readable}`;
}

export function deriveSkillObservations(
  events: readonly KeystrokeEvent[],
  contextId: string,
  observedAt = new Date().toISOString(),
): SkillObservation[] {
  const observations: SkillObservation[] = [];
  const printableWindow: KeystrokeEvent[] = [];

  for (const event of events) {
    if (event.expected === null || event.actual === null) {
      continue;
    }

    const previousPrintable = printableWindow.at(-1) ?? null;
    const positionsAreContiguous =
      previousPrintable !== null &&
      previousPrintable.sourceIndex !== null &&
      event.sourceIndex !== null &&
      event.sourceIndex === previousPrintable.sourceIndex + 1;
    if (previousPrintable !== null && !positionsAreContiguous) {
      printableWindow.length = 0;
    }
    const contiguousPrevious = printableWindow.at(-1) ?? null;
    const contiguousPreviousTwo = printableWindow.at(-2) ?? null;
    const latencyMs =
      previousPrintable === null
        ? null
        : Math.max(0, event.activeElapsedMs - previousPrintable.activeElapsedMs);
    const skillType = classifyKey(event.expected);
    observations.push({
      skillId: keySkillId(event.expected),
      skillType,
      sessionId: event.sessionId,
      contextId,
      correctInitial: event.isCorrectAtEntry === true,
      corrected: event.isCorrection,
      latencyMs,
      observedAt,
    });

    if (contiguousPrevious?.expected !== null && contiguousPrevious !== null) {
      observations.push({
        skillId: `bigram:${contiguousPrevious.expected}${event.expected}`,
        skillType: "bigram",
        sessionId: event.sessionId,
        contextId,
        correctInitial:
          contiguousPrevious.isCorrectAtEntry === true &&
          event.isCorrectAtEntry === true,
        corrected:
          contiguousPrevious.isCorrection || event.isCorrection,
        latencyMs,
        observedAt,
      });
    }

    if (
      contiguousPreviousTwo?.expected !== null &&
      contiguousPreviousTwo !== null &&
      contiguousPrevious?.expected !== null &&
      contiguousPrevious !== null
    ) {
      observations.push({
        skillId: `trigram:${contiguousPreviousTwo.expected}${contiguousPrevious.expected}${event.expected}`,
        skillType: "trigram",
        sessionId: event.sessionId,
        contextId,
        correctInitial:
          contiguousPreviousTwo.isCorrectAtEntry === true &&
          contiguousPrevious.isCorrectAtEntry === true &&
          event.isCorrectAtEntry === true,
        corrected:
          contiguousPreviousTwo.isCorrection ||
          contiguousPrevious.isCorrection ||
          event.isCorrection,
        latencyMs,
        observedAt,
      });
    }

    printableWindow.push(event);
    if (printableWindow.length > 2) {
      printableWindow.shift();
    }
  }

  return observations;
}

function emptyMastery(
  observation: SkillObservation,
  nowIso: string,
): SkillMastery {
  return {
    skillId: observation.skillId,
    skillType: observation.skillType,
    attempts: 0,
    correctInitialAttempts: 0,
    correctedAttempts: 0,
    meanLatencyMs: null,
    p50LatencyMs: null,
    p90LatencyMs: null,
    accuracy: null,
    masteryScore: 0,
    confidence: 0,
    sessionCount: 0,
    contextCount: 0,
    lastPracticedAt: null,
    lastUpdatedAt: nowIso,
    algorithmVersion: "mastery-1.1.0",
  };
}

export function updateSkillMastery(
  current: SkillMastery | null,
  observations: readonly SkillObservation[],
  options: {
    now?: Date;
    userMedianLatencyMs?: number | null;
  } = {},
): SkillMastery {
  if (observations.length === 0 && current === null) {
    throw new Error("At least one observation is required for new mastery.");
  }

  const now = options.now ?? new Date();
  const nowIso = now.toISOString();
  const first = observations[0];
  if (!first) {
    return { ...current!, lastUpdatedAt: nowIso };
  }

  const base = current ?? emptyMastery(first, nowIso);
  const attempts = base.attempts + observations.length;
  const correctInitialAttempts =
    base.correctInitialAttempts +
    observations.filter((item) => item.correctInitial).length;
  const correctedAttempts =
    base.correctedAttempts +
    observations.filter((item) => item.corrected).length;
  const newLatencies = observations
    .map((item) => item.latencyMs)
    .filter((value): value is number => value !== null);
  const existingLatencyWeight =
    base.meanLatencyMs === null ? 0 : Math.max(0, base.attempts - 1);
  const totalLatencyWeight = existingLatencyWeight + newLatencies.length;
  const meanLatencyMs =
    totalLatencyWeight === 0
      ? null
      : round(
          ((base.meanLatencyMs ?? 0) * existingLatencyWeight +
            newLatencies.reduce((sum, value) => sum + value, 0)) /
            totalLatencyWeight,
        );
  const latencySample = [
    ...(base.p50LatencyMs === null ? [] : [base.p50LatencyMs]),
    ...newLatencies,
  ];
  const sessionIds = new Set(observations.map((item) => item.sessionId));
  const contextIds = new Set(observations.map((item) => item.contextId));
  const sessionCount = base.sessionCount + sessionIds.size;
  const contextCount = base.contextCount + contextIds.size;
  const accuracy = attempts > 0 ? (correctInitialAttempts / attempts) * 100 : 0;
  const minimumAttempts = MINIMUM_ATTEMPTS[base.skillType];
  const attemptConfidence = Math.min(1, attempts / minimumAttempts);
  const sessionConfidence = Math.min(1, sessionCount / 2);
  const contextConfidence = Math.min(1, contextCount / 2);
  const confidence = round(
    attemptConfidence * sessionConfidence * (0.75 + contextConfidence * 0.25),
  );
  const userMedianLatencyMs =
    options.userMedianLatencyMs ?? base.p50LatencyMs ?? meanLatencyMs;
  const latencyRatio =
    meanLatencyMs !== null && userMedianLatencyMs !== null && userMedianLatencyMs > 0
      ? meanLatencyMs / userMedianLatencyMs
      : 1;
  const speedScore = Math.max(0, Math.min(100, 115 - latencyRatio * 35));
  const lastPracticedAt =
    observations[observations.length - 1]?.observedAt ??
    base.lastPracticedAt;
  const ageDays = lastPracticedAt
    ? Math.max(0, (now.getTime() - new Date(lastPracticedAt).getTime()) / 86_400_000)
    : 365;
  const recencyScore = Math.max(50, 100 - ageDays * 2);
  const qualityScore = accuracy * 0.65 + speedScore * 0.2 + recencyScore * 0.15;
  let masteryScore = Math.max(
    0,
    Math.min(100, qualityScore * (0.45 + confidence * 0.55)),
  );

  if (sessionCount < 2) {
    masteryScore = Math.min(84, masteryScore);
  }

  return {
    ...base,
    attempts,
    correctInitialAttempts,
    correctedAttempts,
    meanLatencyMs,
    p50LatencyMs: percentile(latencySample, 50),
    p90LatencyMs: percentile(latencySample, 90),
    accuracy: round(accuracy),
    masteryScore: round(masteryScore),
    confidence,
    sessionCount,
    contextCount,
    lastPracticedAt,
    lastUpdatedAt: nowIso,
    algorithmVersion: "mastery-1.1.0",
  };
}

export function applyMasteryObservations(
  current: readonly SkillMastery[],
  observations: readonly SkillObservation[],
  options: {
    now?: Date;
    userMedianLatencyMs?: number | null;
  } = {},
): SkillMastery[] {
  const bySkill = new Map(current.map((item) => [item.skillId, item]));
  const grouped = new Map<string, SkillObservation[]>();
  for (const observation of observations) {
    const list = grouped.get(observation.skillId) ?? [];
    list.push(observation);
    grouped.set(observation.skillId, list);
  }

  for (const [skillId, skillObservations] of grouped) {
    bySkill.set(
      skillId,
      updateSkillMastery(bySkill.get(skillId) ?? null, skillObservations, options),
    );
  }

  return [...bySkill.values()].sort((left, right) =>
    left.skillId.localeCompare(right.skillId),
  );
}

export function masteryBand(score: number): MasteryBand {
  if (score < 25) return "New";
  if (score < 50) return "Developing";
  if (score < 70) return "Functional";
  if (score < 85) return "Strong";
  return "Mastered";
}
